import { useEffect, useState } from "react";
import Loader from "react-loader-spinner";

import { Api } from "../../services/api";
import { ChartTile } from "../../components/ChartTile";
import { InformationTile } from "../../components/InformationTile";
import { PageDateSelector } from "../../components/PageDateSelector";
import { Table } from "../../components/Table";
import { handleAxiosError } from "../../services/utils/handleAxiosError";
import { useAppSelector } from "../../store/hooks";
import { getColumns } from "./columnData";
import {
  ChartContainer,
  Container,
  Content,
  Header,
  Spinner,
  TableContainer,
  TableContent,
  TableTitle,
  TileContainer,
  Title,
} from "./styles";
import { useParams } from "react-router-dom";
import { DatePermissionParams } from "../../services/api/types/Dashboards";
import { useMemberDetails } from "../../services/hooks/MemberDetails";
import { makeName } from "../../services/hooks/MembersList";

export const TeamsDashboard = () => {
  const token = useAppSelector(state => state.auth.token);
  const isExec = false;
  const { teamLeadId } = useParams<{ teamLeadId: string }>();
  const memberDetails = useMemberDetails(teamLeadId);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [chartsLoading, setChartsLoading] = useState<boolean>(true);
  const [dashboard, setDashboard] = useState<any>({
    averageComp: { amount: null, departments: [] },
    averageHours: { hours: null, teams: [] },
    averageTicket: { amount: null, departments: [] },
    leaderScore: { score: null, teams: [] },
    mamSales: { amount: null, departments: [] },
    members: [],
    monthlyCheckins: { completed: null, percentage: null, teams: [] },
    overtimeWeeks: { weeks: null, teams: [] },
    referrals: { amount: null },
    timeOff: { hours: null, teams: [], departments: [] },
    winRate: { percentage: null },
  });

  const [dateRange, setDateRange] = useState<DatePermissionParams>({
    executive: false,
    teamLeadId: teamLeadId,
    is30Day: true,
  });

  useEffect(() => {
    fetchDashboard(dateRange);
  }, [dateRange]);

  const fetchDashboard = async (dateRange: DatePermissionParams) => {
    setIsLoading(true);

    try {
      const dashboard = await Api.dashboards.getDashboard(dateRange);
      setDashboard(dashboard.data);
    } catch (error) {
      console.error("Dashboard Fetch:", error);
      handleAxiosError(error);
    }

    setIsLoading(false);

    // Utilize the the spinner on mount and utilize chart animation for subsequent date selections
    setChartsLoading(false);
  };

  const formatNumber = (number: number, type: string) => {
    //fix number to being a 0 if it is not a number
    if (typeof number === "string") {
      number = parseFloat(number);
    }
    if (typeof number !== "number") number = 0;
    if (type === "percent") {
      return Math.round(number * 100) + "%";
    } else if (type === "dollar") {
      return "$" + Math.round(number).toLocaleString();
    }
  };

  return (
    <Container>
      <Content>
        <Title>Teams : {memberDetails && makeName(memberDetails.member)}</Title>

        <Header isExec={isExec}>
          <PageDateSelector
            alignRight={isExec ? true : false}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        </Header>

        <TileContainer>
          <InformationTile
            title={"My Leader Score"}
            value={((dashboard.leaderScore.score as number) || 0).toFixed(2)}
            isLoading={isLoading}
          />
          <InformationTile
            title={"Monthly Check-ins"}
            value={`${dashboard.monthlyCheckins.completed}:
              ${formatNumber(dashboard.monthlyCheckins.percentage, "percent")}`}
            isLoading={isLoading}
          />
          <InformationTile
            title={"Weeks in Overtime"}
            value={dashboard.overtimeWeeks.weeks}
            isLoading={isLoading}
          />
          <InformationTile
            title={"Time Off"}
            value={dashboard.timeOff.hours}
            hoverStateData={dashboard.timeOff.departments}
            hoverTitle={"name"}
            hoverValue={"hours"}
            isLoading={isLoading}
          />
        </TileContainer>

        <ChartContainer>
          {chartsLoading ? (
            <Spinner>
              <Loader type="Oval" color="#00BFFF" height={40} width={40} />
            </Spinner>
          ) : (
            <>
              <ChartTile
                title="Average Work Week (Hrs)"
                data={dashboard.averageHours.teams}
                type="leaderScore"
                dataField="hours"
              />
              <ChartTile
                title="Monthly Checkins"
                data={dashboard.monthlyCheckins.teams}
                type="checkins"
                dataField="completed"
              />
              <ChartTile
                title="Overtime Weeks"
                data={dashboard.overtimeWeeks.teams}
                type="overtimeWeeks"
                dataField="weeks"
              />
              <ChartTile
                title="Time Off/Missed (Hrs)"
                data={dashboard.timeOff.teams}
                type="timeOff"
                dataField="hours"
              />
            </>
          )}
        </ChartContainer>

        <TileContainer>
          <InformationTile
            title={"Win Rate"}
            width={"206px"}
            value={
              dashboard.winRate.percentage &&
              formatNumber(dashboard.winRate.percentage, "percent")
            }
            isLoading={isLoading}
          />
          <InformationTile
            title={"Average Ticket"}
            width={"206px"}
            value={formatNumber(dashboard.averageTicket.amount, "dollar")}
            hoverStateData={dashboard.averageTicket.departments}
            hoverTitle={"name"}
            hoverValue={"amount"}
            hoverValueType={"dollar"}
            isLoading={isLoading}
          />
          <InformationTile
            title={"Average Comp"}
            width={"206px"}
            value={formatNumber(dashboard.averageComp.amount, "dollar")}
            hoverStateData={dashboard.averageComp.departments}
            hoverTitle={"name"}
            hoverValue={"amount"}
            hoverValueType={"dollar"}
            isLoading={isLoading}
          />
          <InformationTile
            title={"Advantage Sales"}
            width={"206px"}
            value={formatNumber(dashboard.mamSales.amount, "percent")}
            hoverStateData={dashboard.mamSales.departments}
            hoverTitle={"name"}
            hoverValue={"amount"}
            hoverValueType={"percent"}
            isLoading={isLoading}
          />
          {/* <InformationTile */}
          {/*   title={"Referrals"} */}
          {/*   width={"206px"} */}
          {/*   value={formatNumber(dashboard.referrals.amount, "percent")} */}
          {/*   isLoading={isLoading} */}
          {/* /> */}
        </TileContainer>

        <TableContainer>
          <TableTitle>My Team</TableTitle>
          <TableContent>
            <Table
              tableType="Dashboards"
              tableWidth="1108px"
              isLoading={isLoading}
              defaultRows={10}
              columns={getColumns(token)}
              rowsPerPage={[10, 20, 35, 50]}
              data={dashboard.members}
            />
          </TableContent>
        </TableContainer>
      </Content>
    </Container>
  );
};
